export const telephonyEn = {
  admin: {
    title: 'Telephony & AI control centre',
    subtitle: 'Audio processing, transcription service, language model and per-user activation.',
    tabEngine: 'Audio & AI engine',
    tabUsers: 'User activation',
    error: 'The configuration could not be loaded.',
    saved: 'The configuration has been saved.',
    engineTitle: 'Audio & STT engine',
    engineHint:
      'The recording is fetched after the call, transcribed and then analysed by the language model.',
    enabled: 'AI analysis active',
    enabledHint: 'With analysis switched off, recordings are only logged.',
    mode: 'Audio processing mode',
    modeHint:
      'Auto detection selects by channel count: stereo is transcribed per channel, mono is separated by speaker diarisation.',
    whisperUrl: 'Whisper / WhisperX API URL',
    whisperKey: 'Whisper auth token',
    ollamaUrl: 'Ollama server URL',
    ollamaUrlHint:
      'Locally "http://localhost:11434" or on the network "http://192.168.1.50:11434".',
    ollamaModel: 'Ollama model',
    prompt: 'Extraction prompt',
    promptHint: 'The placeholder {{transcript}} is replaced by the formatted speaker transcript.',
    secretSet: 'Stored ({{hint}}). Leaving it empty keeps the saved value.',
    secretUnset: 'No token stored.',
  },
  modes: {
    AUTO_DETECT: 'Auto detection (stereo → faster-whisper / mono → WhisperX)',
    STEREO_SPLIT_FASTER_WHISPER: 'Forced: stereo dual channel (faster-whisper)',
    MONO_DIARIZATION_WHISPERX: 'Forced: mono speaker diarisation (WhisperX)',
    SINGLE_CHANNEL_PLAIN: 'Plain mono without speaker separation',
  },
  test: {
    title: 'Pipeline test',
    subtitle:
      'Upload a test file and verify the entire chain of format detection, transcription and summary.',
    upload: 'Upload test file & verify STT/LLM pipeline',
    running: 'The pipeline is running. Depending on the length this takes a few minutes.',
    success: 'The pipeline completed successfully.',
    failed: 'The pipeline ended with an error.',
    tooLarge: 'The test file exceeds {{size}} MB.',
    transcript: 'Transcript',
    summary: 'Summary',
    checkedAt: 'Checked at {{timestamp}}',
    steps: {
      ANALYSIS: 'Format detection',
      TRANSCRIPTION: 'Transcription',
      SUMMARY: 'Summary',
    },
  },
  users: {
    title: 'Per-user activation',
    subtitle:
      'Telephony is enabled strictly per user account. Without an agent id, direct dial number or extension, inbound calls cannot be matched.',
    error: 'The user list could not be loaded.',
    empty: 'There are no users.',
    saved: 'The activation has been saved.',
    user: 'Employee',
    providerUserId: 'Agent id',
    providerUserIdHint: 'Ringover user id, for example 1234567.',
    numbers: 'Phone numbers',
    extension: 'Internal extension',
    extensionPlaceholder: '101',
    directDial: 'Direct dial (E.164)',
    directDialPlaceholder: '+4961421234512',
    outboundCallerId: 'Outbound CLI',
    outboundCallerIdPlaceholder: '+496142123450',
    outboundCallerIdHint: 'Number shown to the person being called.',
    apiKey: 'Personal API key',
    apiKeyPlaceholder: 'Enter a new key',
    apiKeySet: 'Stored ({{hint}}). Leave empty to keep the saved value.',
    apiKeyUnset: 'No key stored.',
    showKey: 'Show key',
    hideKey: 'Hide key',
    enabled: 'Active',
    toggle: 'Enable telephony for {{name}}',
    test: 'Test',
    testHint: 'Check the connection using the stored credentials',
    testRunning: 'The connection test is running.',
    testSuccess: 'Connection established: {{name}}',
    testSuccessAnonymous: 'The connection to the provider is working.',
    providerHint: 'Supported providers: {{providers}}.',
  },
  webhook: {
    title: 'Ringover webhook endpoint',
    hint: 'Enter this URL and the secret in the Ringover developer dashboard to receive call events and recordings in real time.',
    url: 'Webhook URL',
    copy: 'Copy',
    copied: 'The webhook URL has been copied.',
    copyFailed: 'The URL could not be copied. Please select it manually.',
    secret: 'Global webhook secret',
    secretPlaceholder: 'Enter a new secret',
    secretHint:
      'Signing key for all inbound deliveries. It applies to the entire installation because Ringover uses exactly one secret per integration.',
    secretSet: 'Stored ({{hint}}). Leave empty to keep the saved value.',
    secretUnset: 'No secret stored yet — inbound deliveries are rejected.',
    masterKey: 'Ringover admin API key (optional)',
    masterKeyHint: 'Used for installation-wide queries and not strictly required.',
    save: 'Save',
    saved: 'The provider configuration has been saved.',
    error: 'The provider configuration could not be loaded.',
  },
  list: {
    subtitle: 'Calls with this business partner including transcript and AI analysis.',
    error: 'The call history could not be loaded.',
    emptyTitle: 'No calls recorded yet',
    emptyHint: 'As soon as a call has taken place it will appear here.',
    selectHint: 'Select a call to see its analysis.',
    showTranscript: 'Show transcript',
    callNow: 'Call {{number}}',
    noPermission: 'You are not permitted to view the call history.',
  },
  fields: {
    startedAt: 'Time',
    direction: 'Direction',
    agent: 'Agent',
    status: 'Status',
    duration: 'Duration (min)',
    actions: 'Actions',
  },
  directions: {
    INBOUND: 'Inbound',
    OUTBOUND: 'Outbound',
  },
  statuses: {
    RINGING: 'Ringing',
    ANSWERED: 'Answered',
    MISSED: 'Missed',
    VOICEMAIL: 'Voicemail',
    FAILED: 'Failed',
  },
  sentiments: {
    POSITIVE: 'Positive',
    NEUTRAL: 'Neutral',
    NEGATIVE: 'Negative',
    DISPUTED: 'Disputed',
  },
  processing: {
    PENDING: 'Analysis is queued.',
    DOWNLOADING_AUDIO: 'Fetching the recording …',
    TRANSCRIBING: 'Transcribing the call …',
    SUMMARIZING: 'The language model is summarising …',
    COMPLETED: 'Analysis completed.',
    FAILED: 'The analysis failed.',
    DISABLED: 'AI analysis is switched off system-wide.',
  },
  summary: {
    title: 'AI summary',
    empty: 'No summary is available for this call.',
    edit: 'Edit summary',
    editorLabel: 'Summary',
    saved: 'The summary has been saved.',
    editedBadge: 'Edited by {{name}} on {{timestamp}}',
    showOriginal: 'Show original AI text',
    actionItems: 'Action items',
    reprocess: 'Restart analysis',
    reprocessStarted: 'The analysis has been restarted.',
  },
  transcript: {
    title: 'Call transcript',
    subtitle: 'Processing mode: {{mode}}',
    modeUnknown: 'unknown',
    empty: 'No transcript is available for this call.',
    showPlain: 'Plain text',
    showDialog: 'Dialogue',
    agent: 'Agent',
    customer: 'Customer',
  },
  screenPop: {
    title: 'Incoming call',
    unknown: 'No business partner is stored for this number.',
    openDocuments: '{{count}} open documents',
    lastCall: 'Last call: {{timestamp}}',
    open: 'Open contact',
    blockedTitle: '🚨 Locked: {{status}}',
    blockedReason: 'Reason: {{reason}}',
  },
  call: {
    started: 'The call has started. Please answer it on your device.',
    failed: 'The call could not be started: {{message}}',
  },
} as const;
